import React, { useEffect, useState } from 'react';
import styles from './AdBanner.module.css';
import { axiosInstance } from '../utils/axiosConfig';

interface Ad {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  clickUrl: string;
  tier: string;
  priority: number;
}

interface AdBannerProps {
  autoRotateInterval?: number; // milliseconds
  /**
   * Phase 15.3 — ad placement slot (design 10.3). The backend frequency-caps
   * per slot. Defaults to 'default', preserving pre-existing behavior for
   * the dashboard banner.
   */
  slot?: string;
}

const DISMISSED_ADS_KEY = 'sumbitrbuddy_dismissed_ads';
const DISMISS_EXPIRY_DAYS = 7; // Ads can be re-shown after 7 days

// Phase 9.2 — Per-session id (sessionStorage-backed, regenerated on tab close)
// so the backend frequency cap can scope impressions correctly.
const SESSION_ID_KEY = 'hb_ad_session_id';
function getAdSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return 'no-session';
  }
}

/**
 * AdBanner Component
 * Displays ads to Free tier users with:
 * - Frequency capping (dismissed ads hidden for 7 days)
 * - Auto-rotation between available ads
 * - Dismiss button
 * - Click tracking
 * - Impression tracking
 * - Responsive design
 */
const AdBanner: React.FC<AdBannerProps> = ({ autoRotateInterval = 5000, slot = 'default' }) => {
  const [ads, setAds] = useState<Ad[]>([]);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissedAds, setDismissedAds] = useState<Set<string>>(new Set());
  const [isVisible, setIsVisible] = useState(true);

  // Load dismissed ads from localStorage (with expiry)
  useEffect(() => {
    const loadDismissedAds = () => {
      try {
        const stored = localStorage.getItem(DISMISSED_ADS_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as { [key: string]: number };
          const now = Date.now();
          const freshDismissals = new Set<string>();

          // Only keep ads that were dismissed within the last 7 days
          Object.entries(parsed).forEach(([adId, dismissedAt]) => {
            const daysSinceDismissal = (now - dismissedAt) / (1000 * 60 * 60 * 24);
            if (daysSinceDismissal < DISMISS_EXPIRY_DAYS) {
              freshDismissals.add(adId);
            }
          });

          setDismissedAds(freshDismissals);
          
          // Update localStorage with only fresh dismissals
          if (freshDismissals.size !== Object.keys(parsed).length) {
            const updated: { [key: string]: number } = {};
            freshDismissals.forEach(adId => {
              updated[adId] = parsed[adId];
            });
            localStorage.setItem(DISMISSED_ADS_KEY, JSON.stringify(updated));
          }
        }
      } catch (e) {
        console.error('Error loading dismissed ads:', e);
      }
    };

    loadDismissedAds();
  }, []);

  // Fetch available ads
  useEffect(() => {
    const fetchAds = async () => {
        try {
          setLoading(true);
          const resp = await axiosInstance.get('/ads', {
            params: { slot },
            headers: { 'X-Session-Id': getAdSessionId() },
          });
          const data = resp.data;
          if (data.success && Array.isArray(data.data) && data.data.length > 0) {
            setAds(data.data);
            setError(null);
          } else {
            setAds([]);
          }
        } catch (err) {
          console.error('Error fetching ads:', err);
          setError(err instanceof Error ? err.message : 'Failed to load ads');
          setAds([]);
        } finally {
          setLoading(false);
        }
    };

    fetchAds();
  }, [slot]);

  // Track impression when ad is displayed
  useEffect(() => {
    if (ads.length === 0 || !isVisible) return;

    const currentAd = ads[currentAdIndex];
    if (!currentAd || dismissedAds.has(currentAd.id)) return;

    const recordImpression = async () => {
      try {
        await axiosInstance.post(
          `/ads/${currentAd.id}/impression`,
          {},
          { headers: { 'X-Session-Id': getAdSessionId() } }
        );
      } catch (err) {
        console.error('Error recording impression:', err);
      }
    };

    recordImpression();
  }, [ads, currentAdIndex, dismissedAds, isVisible]);

  // Auto-rotate ads
  useEffect(() => {
    if (ads.length <= 1 || !isVisible) return;

    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [ads.length, autoRotateInterval, isVisible]);

  const handleAdClick = async () => {
    const currentAd = ads[currentAdIndex];
    if (!currentAd) return;

      try {
        // Track click
        await axiosInstance.post(
          `/ads/${currentAd.id}/click`,
          {},
          { headers: { 'X-Session-Id': getAdSessionId() } }
        );

        // Open ad URL in new tab
        window.open(currentAd.clickUrl, '_blank');
      } catch (err) {
        console.error('Error recording click:', err);
        // Still open the URL even if tracking fails
        window.open(currentAd.clickUrl, '_blank');
      }
  };

  const handleDismiss = () => {
    const currentAd = ads[currentAdIndex];
    if (!currentAd) return;

    // Add to dismissed set
    const newDismissed = new Set(dismissedAds);
    newDismissed.add(currentAd.id);
    setDismissedAds(newDismissed);

    // Store in localStorage with timestamp
    const stored = localStorage.getItem(DISMISSED_ADS_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[currentAd.id] = Date.now();
    localStorage.setItem(DISMISSED_ADS_KEY, JSON.stringify(parsed));

    // Move to next available ad
    const availableAds = ads.filter(ad => !newDismissed.has(ad.id));
    if (availableAds.length === 0) {
      setIsVisible(false);
    } else {
      setCurrentAdIndex((prev) => (prev + 1) % ads.length);
    }
  };

  // Filter out dismissed ads
  const availableAds = ads.filter(ad => !dismissedAds.has(ad.id));

  if (!isVisible || availableAds.length === 0 || loading) {
    return null;
  }

  if (error) {
    console.error('AdBanner error:', error);
    return null;
  }

  const currentAd = availableAds[currentAdIndex % availableAds.length];
  if (!currentAd) return null;

  return (
    <div className={styles.adBanner}>
      <div className={styles.adContainer}>
        {currentAd.imageUrl && (
          <img 
            src={currentAd.imageUrl} 
            alt={currentAd.title} 
            className={styles.adImage}
          />
        )}
        <div className={styles.adContent}>
          <h3 className={styles.adTitle}>{currentAd.title}</h3>
          {currentAd.description && (
            <p className={styles.adDescription}>{currentAd.description}</p>
          )}
          <button
            className={styles.adButton}
            onClick={handleAdClick}
            aria-label={`View ${currentAd.title}`}
          >
            Learn More →
          </button>
        </div>
        <button
          className={styles.dismissButton}
          onClick={handleDismiss}
          aria-label="Dismiss ad"
          title="Hide this ad for 7 days"
        >
          ✕
        </button>
      </div>

      {/* Pagination dots */}
      {availableAds.length > 1 && (
        <div className={styles.pagination}>
          {availableAds.map((_, index) => (
            <button
              key={_.id}
              className={`${styles.dot} ${index === currentAdIndex % availableAds.length ? styles.active : ''}`}
              onClick={() => setCurrentAdIndex(index)}
              aria-label={`Go to ad ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdBanner;
